import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Spin, Divider, List, Tag, Space, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import * as partnerApi from '../../modules/partners/api';

const { TextArea } = Input;

const PartnerForm = ({ handleSubmit, action, partner, handleCancel, isFetching }) => {
  const [form] = Form.useForm();
  const [itemTypes, setItemTypes] = useState([]);
  const [newItemTypeName, setNewItemTypeName] = useState('');
  const [itemTypesLoading, setItemTypesLoading] = useState(false);

  const onFinishForm = (values) => {
    handleSubmit && handleSubmit(values);
  };

  useEffect(() => {
    if (action === 'new') {
      form.setFieldsValue({
        name: null,
        contactEmail: null,
        contactPhone: null,
        address: null,
        notes: null,
      });
      setItemTypes([]);
    } else if (partner) {
      form.setFieldsValue({
        name: partner.name,
        contactEmail: partner.contactEmail,
        contactPhone: partner.contactPhone,
        address: partner.address,
        notes: partner.notes,
      });
      // Load partner item types for existing partner
      loadItemTypes(partner.id);
    }
  }, [action, partner]);

  const loadItemTypes = async (partnerId) => {
    setItemTypesLoading(true);
    const { response } = await partnerApi.getPartnerItemTypes(partnerId);
    if (response) {
      setItemTypes(response);
    }
    setItemTypesLoading(false);
  };

  const handleAddItemType = async () => {
    if (!newItemTypeName.trim() || !partner?.id) return;
    setItemTypesLoading(true);
    const { response } = await partnerApi.createPartnerItemType(partner.id, {
      name: newItemTypeName.trim(),
    });
    if (response) {
      setItemTypes([...itemTypes, response]);
      setNewItemTypeName('');
    }
    setItemTypesLoading(false);
  };

  const handleRemoveItemType = async (id) => {
    setItemTypesLoading(true);
    const { response } = await partnerApi.removePartnerItemType(id);
    if (response) {
      setItemTypes(itemTypes.filter((t) => t.id !== id));
    }
    setItemTypesLoading(false);
  };

  return (
    <Spin spinning={isFetching}>
      <div className="RiderForm">
        <div className="FormHeader"></div>
        <div className="FormContent">
          <Form
            form={form}
            scrollToFirstError
            layout="vertical"
            requiredMark={false}
            colon={false}
            onFinish={onFinishForm}
          >
            <Form.Item
              label="Partner Name"
              name="name"
              rules={[{ required: true, message: 'Partner name is required' }]}
            >
              <Input placeholder="e.g. Migros, Sterilease" />
            </Form.Item>

            <Form.Item label="Contact Email" name="contactEmail" rules={[{ type: 'email', message: 'Invalid email' }]}>
              <Input placeholder="ops@partner.com" />
            </Form.Item>

            <Form.Item label="Contact Phone" name="contactPhone">
              <Input placeholder="+41..." />
            </Form.Item>

            <Form.Item label="Address" name="address">
              <Input placeholder="Partner address" />
            </Form.Item>

            <Form.Item label="Notes" name="notes">
              <TextArea rows={3} placeholder="Internal notes about this partner" />
            </Form.Item>
          </Form>

          {/* Returnable Item Types — only in edit mode */}
          {action === 'edit' && partner?.id && (
            <>
              <Divider />
              <div style={{ marginBottom: 8 }}>
                <strong>Returnable Item Types</strong>
                <span style={{ color: '#9E9E9E', marginLeft: 8, fontSize: 12 }}>
                  Items drivers can collect from this partner&apos;s customers
                </span>
              </div>

              <Spin spinning={itemTypesLoading}>
                <List
                  size="small"
                  bordered
                  dataSource={itemTypes}
                  locale={{ emptyText: 'No item types defined yet' }}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Tag key="active" color={item.isActive !== false ? 'green' : 'default'}>
                          {item.isActive !== false ? 'Active' : 'Inactive'}
                        </Tag>,
                        <Popconfirm
                          key="delete"
                          title="Remove this item type?"
                          onConfirm={() => handleRemoveItemType(item.id)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>,
                      ]}
                    >
                      <List.Item.Meta
                        title={item.name}
                        description={item.codePrefix ? `Code prefix: ${item.codePrefix}` : null}
                      />
                      {item._count?.collectedItems > 0 && <Tag>{item._count.collectedItems} collected</Tag>}
                    </List.Item>
                  )}
                />

                <Space style={{ marginTop: 12, width: '100%' }}>
                  <Input
                    value={newItemTypeName}
                    onChange={(e) => setNewItemTypeName(e.target.value)}
                    placeholder="New item type name (e.g. Blue crate)"
                    onPressEnter={handleAddItemType}
                    style={{ width: 300 }}
                  />
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={handleAddItemType}
                    disabled={!newItemTypeName.trim()}
                  >
                    Add
                  </Button>
                </Space>
              </Spin>
            </>
          )}
        </div>

        <div className="FormFooter">
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="primary" block size="large" onClick={() => form.submit()}>
            {action === 'new' ? 'Create Partner' : 'Update Partner'}
          </Button>
        </div>
      </div>
    </Spin>
  );
};

export default PartnerForm;
